import {
  Alert,
  Backdrop,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import { RiCloseLine } from '@remixicon/react';
import { useTranslation } from 'react-i18next';
import type { useHabilitarBuzonJudicial, ResumenActivacionAgrupada } from '@/shared/hooks/useHabilitarBuzonJudicial.hook';
import SeleccionInstanciaPorCasoLista from './SeleccionInstanciaPorCasoLista';
import CategoriaTribunalSelect from './CategoriaTribunalSelect';
import HabilitarModoToggle from '../HabilitarModoToggle';

interface HabilitarBuzonModalProps {
  habilitar: ReturnType<typeof useHabilitarBuzonJudicial>;
  onConfirmed: (resumen: ResumenActivacionAgrupada) => void;
}

/**
 * Modal "Habilitar por:" — permite elegir habilitar el Buzón Judicial por NUC (caso completo, comportamiento
 * actual) o por Instancia (una o varias categorías de tribunal). Es "tonto": toda la lógica de negocio vive
 * en useHabilitarBuzonJudicial; este componente solo pinta el estado del hook y reenvía sus acciones.
 *
 * Diseño moderno y minimalista: el NUC/conteo de seleccionados es un badge informativo (no un input
 * falso), "Habilitar por" es un segmented control (2 opciones excluyentes, no una lista de radios), los
 * labels son texto simple arriba del campo (no legends flotantes), y el estado incompleto se comunica
 * con borde + texto de ayuda en cada tarjeta en vez de solo deshabilitar "Confirmar" sin explicación.
 */
const HabilitarBuzonModal = ({ habilitar, onConfirmed }: HabilitarBuzonModalProps) => {
  const { t } = useTranslation();
  const {
    isOpen,
    casos,
    origen,
    modo,
    esMultiple,
    categoriasSeleccionadas,
    seleccionPorCaso,
    isLoadingInstancias,
    isErrorInstancias,
    refetchInstancias,
    canConfirmar,
    isConfirming,
    cerrarModal,
    setModo,
    setCategoriasSeleccionadas,
    setCategoriaParaCaso,
    confirmar,
    filtrarCategoriasPorCaso,
    filtrarCategoriasNuevasParaCaso,
  } = habilitar;

  const esAgregarNuevaInstancia = origen === 'nueva';
  const mostrarSelectorInstancia = modo === 'INSTANCIA' || esAgregarNuevaInstancia;
  const listaMultiple = esMultiple && !esAgregarNuevaInstancia;

  const tituloModal = esAgregarNuevaInstancia
    ? t('buzonJudicial.enableModal.addInstanceTitle', 'Habilitar nueva instancia')
    : esMultiple
      ? t('buzonJudicial.enableModal.multipleTitle', 'Habilitar NUCs seleccionados')
      : t('buzonJudicial.enableModal.singleTitle', 'Habilitar NUC');

  const nucBadgeLabel = esMultiple
    ? t('buzonJudicial.modal.selectedNucs', { count: casos.length, defaultValue: `${casos.length} NUC(s) seleccionados` })
    : casos[0]?.nuc ?? '';

  const opcionesCategoriaDisponibles = esAgregarNuevaInstancia
    ? filtrarCategoriasNuevasParaCaso(casos[0])
    : casos[0]
      ? filtrarCategoriasPorCaso(casos[0].idCaso)
      : [];

  const sinOpcionesCasoUnico = (esAgregarNuevaInstancia || !listaMultiple) && opcionesCategoriaDisponibles.length === 0;
  const faltaCompletarSeleccion = mostrarSelectorInstancia && !isLoadingInstancias && !isErrorInstancias && !canConfirmar && !sinOpcionesCasoUnico;
  const helperFooterText = !faltaCompletarSeleccion
    ? ''
    : listaMultiple
      ? t('buzonJudicial.enableModal.completeAllCategoriesHint', 'Completa todas las categorías para continuar')
      : t('buzonJudicial.enableModal.selectCategoryHint', 'Selecciona una categoría para continuar');

  const handleConfirmar = async () => {
    const resumen = await confirmar();
    onConfirmed(resumen);
    if (resumen.fallidos.length === 0) {
      cerrarModal();
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={isConfirming ? undefined : cerrarModal}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          border: '1px solid #e5e7eb',
          maxHeight: '85vh',
        },
      }}
    >
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 10, position: 'absolute', borderRadius: '16px' }} open={isConfirming}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress color="inherit" size={48} thickness={4} />
          <Typography variant="body1" component="p" sx={{ color: '#fff' }}>
            {t('buzonJudicial.messages.processingRequest', 'Procesando solicitud, por favor espere...')}
          </Typography>
        </Box>
      </Backdrop>

      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontWeight: 700, pb: 1 }}>
        {tituloModal}
        <IconButton onClick={cerrarModal} disabled={isConfirming} size="small" sx={{ color: '#6b7280' }}>
          <RiCloseLine size={18} />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Chip
            label={nucBadgeLabel}
            size="small"
            sx={{ alignSelf: 'flex-start', backgroundColor: '#D4EFFF', color: '#0843A0', fontWeight: 700, borderRadius: '999px' }}
          />

          {!esAgregarNuevaInstancia && (
            <HabilitarModoToggle value={modo} onChange={setModo} />
          )}

          {mostrarSelectorInstancia && isLoadingInstancias && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} />
              <Typography sx={{ fontSize: '0.85rem', color: '#6b7280' }}>
                {t('buzonJudicial.enableModal.loadingInstancias', 'Cargando instancias disponibles...')}
              </Typography>
            </Box>
          )}

          {mostrarSelectorInstancia && !isLoadingInstancias && isErrorInstancias && (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={() => refetchInstancias()}>
                  {t('buzonJudicial.enableModal.retry', 'Reintentar')}
                </Button>
              }
            >
              {t('buzonJudicial.enableModal.errorLoadingInstancias', 'No se pudieron cargar las instancias disponibles')}
            </Alert>
          )}

          {mostrarSelectorInstancia && !isLoadingInstancias && !isErrorInstancias && (
            listaMultiple ? (
              <SeleccionInstanciaPorCasoLista
                casos={casos}
                seleccionPorCaso={seleccionPorCaso}
                onChangeCategoria={setCategoriaParaCaso}
                filtrarCategoriasPorCaso={filtrarCategoriasPorCaso}
                disabled={isConfirming}
              />
            ) : (
              <Box>
                <CategoriaTribunalSelect
                  options={opcionesCategoriaDisponibles}
                  value={categoriasSeleccionadas}
                  onChange={setCategoriasSeleccionadas}
                  disabled={isConfirming || opcionesCategoriaDisponibles.length === 0}
                />
                {sinOpcionesCasoUnico && (
                  <Typography sx={{ fontSize: '0.75rem', color: '#b45309', mt: 0.75 }}>
                    {t('buzonJudicial.enableModal.noNewCategoriesAvailable', 'No hay nuevas instancias disponibles para este caso.')}
                  </Typography>
                )}
              </Box>
            )
          )}
        </Box>
      </DialogContent>

      <Divider sx={{ borderColor: '#e5e7eb' }} />

      <DialogActions sx={{ px: 3, py: 1.75, display: 'flex', alignItems: 'center' }}>
        <Typography sx={{ flex: 1, fontSize: '0.78rem', color: '#6b7280' }}>
          {helperFooterText}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={cerrarModal} color="inherit" disabled={isConfirming} sx={{ textTransform: 'none', fontWeight: 600 }}>
            {t('actions.cancel', 'Cancelar')}
          </Button>
          <Button
            variant="contained"
            disabled={!canConfirmar || isConfirming}
            onClick={handleConfirmar}
            startIcon={isConfirming ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {t('actions.confirm', 'Confirmar')}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default HabilitarBuzonModal;
